import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Avatar,
  Button,
  Skeleton,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { 
  AccessTime, 
  Visibility, 
  Comment
} from "@mui/icons-material";
import { Link } from "react-router-dom";
const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// Helper function to extract first image from HTML content
const extractImageFromContent = (htmlContent) => {
  if (!htmlContent) return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const img = doc.querySelector('img');
  return img ? img.getAttribute('src') : null;
};

// Default image to use when post has no image
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80";

// Sample financial images for posts without images
const FINANCIAL_IMAGES = [
  "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80"
];

function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination state
  const [displayedPostsCount, setDisplayedPostsCount] = useState(5);
  const [loadingMore, setLoadingMore] = useState(false);
  const POSTS_PER_PAGE = 5;
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch all posts using the /posts API endpoint
        const postsRes = await axios.get(`${apiUrl}/posts`);
        
        // Process posts to handle images
        const processedPosts = postsRes.data
          .filter(post => post.status === 'published')
          .map(post => {
            if (post.featured_image_name) {
              const baseUrl = apiUrl.replace("/api", "");
              return {
                ...post,
                image: `${baseUrl}/api/uploads/${encodeURIComponent(post.featured_image_name)}`
              };
            }
            
            // Try to extract image from content
            const contentImage = extractImageFromContent(post.description);
            if (contentImage) {
              return { ...post, image: contentImage };
            }
        
            // Fallback to random financial image
            const randomIndex = Math.floor(Math.random() * FINANCIAL_IMAGES.length);
            return { ...post, image: FINANCIAL_IMAGES[randomIndex] };
          })
          // Filter out any posts that might still not have images
          .filter(post => post.image);
        
        setPosts(processedPosts);
        
      } catch (error) {
        console.error("Error fetching posts:", error);
        
        // Mock data for demonstration - Extended to show pagination
        const mockPosts = [
          {
            id: "1",
            title: "Understanding Market Volatility: Strategies for Investors",
            content: "Market volatility is a term used to describe the frequency and magnitude of price movements in financial markets. It's a measure of how much prices fluctuate over time, and it can be caused by a variety of factors...",
            category: "Investing",
            date: "2025-04-15T10:30:00Z",
            author: {
              name: "Jane Smith",
              avatar: "https://randomuser.me/api/portraits/women/44.jpg"
            },
            image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 1245,
            comments: 28
          },
          {
            id: "2",
            title: "The Impact of Inflation on Investment Strategies",
            content: "Inflation is a persistent increase in the general price level of goods and services in an economy over time. When the general price level rises, each unit of currency buys fewer goods and services...",
            category: "Economy",
            date: "2025-04-10T14:20:00Z",
            author: {
              name: "Michael Johnson",
              avatar: "https://randomuser.me/api/portraits/men/32.jpg"
            },
            image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 982,
            comments: 15
          },
          {
            id: "3",
            title: "Diversification: The Key to Risk Management",
            content: "Diversification is a risk management strategy that mixes a wide variety of investments within a portfolio. The rationale behind this technique is that a portfolio constructed of different kinds of assets...",
            category: "Investing",
            date: "2025-04-08T09:15:00Z",
            author: {
              name: "Sarah Williams",
              avatar: "https://randomuser.me/api/portraits/women/67.jpg"
            },
            image: "https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 756,
            comments: 9
          },
          {
            id: "4",
            title: "Cryptocurrency Market Analysis: Trends and Predictions",
            content: "The cryptocurrency market has experienced significant growth and volatility over the past few years. Understanding the trends and making informed predictions is crucial for investors...",
            category: "Crypto",
            date: "2025-04-05T16:45:00Z",
            author: {
              name: "David Chen",
              avatar: "https://randomuser.me/api/portraits/men/15.jpg"
            },
            image: "https://images.unsplash.com/photo-1640340434855-6084b1f4901c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 1567,
            comments: 42
          },
          {
            id: "5",
            title: "Real Estate Investment Trusts (REITs): A Beginner's Guide",
            content: "Real Estate Investment Trusts (REITs) offer investors a way to invest in real estate without directly owning property. This guide covers the basics of REIT investing...",
            category: "Real Estate",
            date: "2025-04-03T11:20:00Z",
            author: {
              name: "Emily Rodriguez",
              avatar: "https://randomuser.me/api/portraits/women/23.jpg"
            },
            image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80",
            views: 892,
            comments: 18
          },
          {
            id: "6",
            title: "ESG Investing: Sustainable Finance for the Future",
            content: "Environmental, Social, and Governance (ESG) investing has gained significant traction as investors seek to align their portfolios with their values...",
            category: "Sustainable Finance",
            date: "2025-04-01T13:30:00Z",
            author: {
              name: "Robert Taylor",
              avatar: "https://randomuser.me/api/portraits/men/45.jpg"
            },
            image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 634,
            comments: 12
          },
          {
            id: "7",
            title: "Options Trading Strategies for Beginners",
            content: "Options trading can be a powerful tool for investors, but it requires understanding of various strategies and risk management techniques...",
            category: "Trading",
            date: "2025-03-30T08:15:00Z",
            author: {
              name: "Lisa Anderson",
              avatar: "https://randomuser.me/api/portraits/women/56.jpg"
            },
            image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 1123,
            comments: 31
          },
          {
            id: "8",
            title: "The Role of Central Banks in Modern Economics",
            content: "Central banks play a crucial role in maintaining economic stability through monetary policy, interest rate management, and financial system oversight...",
            category: "Economics",
            date: "2025-03-28T15:45:00Z",
            author: {
              name: "Mark Thompson",
              avatar: "https://randomuser.me/api/portraits/men/28.jpg"
            },
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 789,
            comments: 22
          },
          {
            id: "9",
            title: "Fintech Revolution: How Technology is Changing Finance",
            content: "The financial technology sector continues to disrupt traditional banking and investment services, offering new solutions for consumers and businesses...",
            category: "Fintech",
            date: "2025-03-25T12:00:00Z",
            author: {
              name: "Anna Kim",
              avatar: "https://randomuser.me/api/portraits/women/34.jpg"
            },
            image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80",
            views: 1456,
            comments: 38
          },
          {
            id: "10",
            title: "Retirement Planning: Building Wealth for the Future",
            content: "Effective retirement planning requires a comprehensive strategy that includes savings, investments, and understanding of retirement accounts...",
            category: "Retirement",
            date: "2025-03-22T10:30:00Z",
            author: {
              name: "James Wilson",
              avatar: "https://randomuser.me/api/portraits/men/52.jpg"
            },
            image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2026&q=80",
            views: 945,
            comments: 25
          },
          {
            id: "11",
            title: "Global Economic Outlook: Challenges and Opportunities",
            content: "The global economy faces various challenges including inflation, geopolitical tensions, and supply chain disruptions, but also presents new opportunities...",
            category: "Global Economy",
            date: "2025-03-20T14:15:00Z",
            author: {
              name: "Sophie Martinez",
              avatar: "https://randomuser.me/api/portraits/women/41.jpg"
            },
            image: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
            views: 1234,
            comments: 33
          },
          {
            id: "12",
            title: "Tax-Efficient Investment Strategies",
            content: "Understanding tax implications of different investment strategies can significantly impact your overall returns. Learn about tax-efficient investing approaches...",
            category: "Tax Planning",
            date: "2025-03-18T09:45:00Z",
            author: {
              name: "Thomas Brown",
              avatar: "https://randomuser.me/api/portraits/men/39.jpg"
            },
            image: "https://images.unsplash.com/photo-1554224154-26032fced8bd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2026&q=80",
            views: 678,
            comments: 16
          }
        ];
        
        setPosts(mockPosts);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Handle Load More functionality
  const handleLoadMore = () => {
    setLoadingMore(true);
    // Simulate loading delay (remove this in production if not needed)
    setTimeout(() => {
      setDisplayedPostsCount(prev => prev + POSTS_PER_PAGE);
      setLoadingMore(false);
    }, 500);
  };

  // Check if there are more posts to load
  const hasMorePosts = displayedPostsCount < posts.length;

  // Get posts to display
  const postsToDisplay = posts.slice(0, displayedPostsCount);

  // Function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Function to strip HTML tags and truncate text
  const stripHtmlAndTruncate = (text, maxLength) => {
    if (!text) return '';
    // Remove HTML tags
    const strippedText = text.replace(/<[^>]*>/g, '');
    // Decode HTML entities
    const decodedText = strippedText
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    return decodedText.length > maxLength ? decodedText.substring(0, maxLength) + '...' : decodedText;
  };

  // Function to truncate text (keeping for backward compatibility)
  const truncateText = (text, maxLength) => {
    return stripHtmlAndTruncate(text, maxLength);
  };

  return (
    <Box sx={{ 
      backgroundColor: "#f8f9fa", 
      minHeight: "100vh",
      pt: { xs: 2, sm: 3, md: 4 },
      pb: { xs: 4, sm: 5, md: 6 }
    }}>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12}>
            
            {loading ? (
              // Skeleton loading state
              Array.from(new Array(3)).map((_, index) => (
                <Card key={index} sx={{ mb: 3, borderRadius: 2 }}>
                  <CardContent>
                    <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 1 }} />
                    <Skeleton variant="text" height={40} width="80%" />
                    <Skeleton variant="text" height={20} width="60%" />
                    <Skeleton variant="text" height={20} width="40%" />
                  </CardContent>
                </Card>
              ))
            ) : posts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  No articles found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for new content
                </Typography>
              </Box>
            ) : (
              <Box className="post-list">
                {postsToDisplay.map((post, index) => (
                  <Card 
                    key={post.id || index} 
                    sx={{ 
                      mb: 4, 
                      borderRadius: 2,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <CardActionArea component={Link} to={`/post/${post.id}`}>
                      <Grid container direction={isMobile ? 'column' : 'row'}>
                        {post.image && (
                          <Grid item xs={12} md={4}>
                            <CardMedia
                              component="img"
                              src={post.image} // use 'src' instead of 'image' when using onError
                              alt={post.title}
                              onError={(e) => {
                                const randomIndex = Math.floor(Math.random() * FINANCIAL_IMAGES.length);
                                e.target.onerror = null;
                                e.target.src = FINANCIAL_IMAGES[randomIndex];
                              }}
                              sx={{ 
                                objectFit: 'cover',
                                height: isMobile ? 200 : '100%',
                                minHeight: { md: 220 }
                              }}
                            />

                          </Grid>
                        )}
                        <Grid item xs={12} md={post.image ? 8 : 12}>
                          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            {(post.category || post.category_name) && (
                              <Chip 
                                label={post.category || post.category_name} 
                                size="small" 
                                color="primary" 
                                sx={{ mb: 2, fontWeight: 500 }}
                              />
                            )}
                            <Typography 
                              variant="h5" 
                              component="h2" 
                              sx={{ 
                                mb: 1.5, 
                                fontWeight: 600,
                                fontSize: { xs: '1.25rem', md: '1.5rem' }
                              }}
                            >
                              {post.title}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ mb: 2 }}
                            >
                              {truncateText(post.content || post.description, isMobile ? 100 : 150)}
                            </Typography>
                            
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              flexWrap: 'wrap',
                              gap: 2,
                              mt: 2
                            }}>
                              {post.author && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar 
                                    src={post.author.avatar} 
                                    alt={post.author.name}
                                    sx={{ width: 24, height: 24, mr: 1 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {post.author.name}
                                  </Typography>
                                </Box>
                              )}
                              
                              {(post.date || post.updatedAt) && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <AccessTime sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(post.date || post.updatedAt)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {post.views !== undefined && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Visibility sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {post.views} views
                                  </Typography>
                                </Box>
                              )}
                              
                              {post.comments !== undefined && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Comment sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {post.comments} comments
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Grid>
                      </Grid>
                    </CardActionArea>
                  </Card>
                ))}
                
                {/* Load More Button - Only show if there are more posts */}
                {hasMorePosts && (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      sx={{ 
                        px: 4,
                        py: 1,
                        borderWidth: '1.5px',
                        '&:hover': {
                          borderWidth: '1.5px',
                        }
                      }}
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
                
                {/* Show message when all posts are loaded */}
                {!hasMorePosts && posts.length > POSTS_PER_PAGE && (
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      You've reached the end of the articles
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
          
        </Grid>
      </Container>
    </Box>
  );
}

export default Home;

